/**
 * route - erkennt die Fallansicht an der Adresse, an EINER Stelle.
 *
 * 🔴 **Warum es diese Datei gibt.** Die Fallansicht traegt ihre eigene, ruhige
 * Telefonbedienung (Owner-Entscheid 43, woertlich: *"Mobile view muss nochmal in
 * richtiung Button-Dichte, Design etc geprueft werden - da sind viele Buttons zu
 * gross, bzw brechen um, insgesamt viel zu unruhig auf Mobile - da koennen wir
 * nicht einfach die gleiche Interaktion in schmal nutzen!"*). Der Klickdummy
 * `ku-41-fall-telefon.png` zeigt sie: unten steht **nur** der Verfasser, **keine**
 * schwebende Icon-Leiste; zurueck geht es ueber den Pfeil im Fallkopf
 * (`fall-zurueck`).
 *
 * Die Leiste gehoert dem Rahmen (`MobileBottomNav`), nicht der Ansicht. Damit
 * der Rahmen sie auf genau dieser Route auslassen kann, braucht er die Route -
 * und die steht hier, einmal. Ein zweiter Ort (etwa ein `/neu`-Test in der
 * Rahmen-Datei) waere eine zweite Wahrheit und liefe beim naechsten Umbau der
 * Route auseinander.
 *
 * 🔴 **Das Muster kommt aus der Routentabelle, nicht aus einer Textsuche.**
 * `App.js` registriert die Ansicht als `rma/:id`; `matchPath` liest genau diese
 * Form. Ein `endsWith('/neu')` traefe auch `/rma/new` (den Anlegepfad) oder
 * `/irgendwas/neu` und waere damit weiter als die Route, die es meint.
 *
 * 🔴 **`rma/:id` faengt aber auch die STATISCHEN Geschwister** (`/rma/inbox`,
 * `/rma/new`, `/rma/reports`, ...). `matchPath` kennt die Rangfolge des Routers
 * nicht - der Router laesst eine statische Route gewinnen, `matchPath` nicht.
 * Ohne die Ausnahmeliste unten meldete `istFallansicht('/rma/inbox')` **true**,
 * und der Rahmen liesse seine Telefonleiste auf der Inbox und im Anlegepfad
 * aus: eine stille Regression, weil beide Seiten unter beiden Annahmen
 * dasselbe Bild zeigen. Die Liste ist die eine Stelle, an der diese zweite
 * Wahrheit steht; `route.test.js` haelt sie gegen `App.js` (Regel 17).
 */
import { matchPath } from 'react-router-dom'

/**
 * Die Adresse der neuen Fallansicht - woertlich wie in `App.js`.
 *
 * 🔴 **Seit W4 (2026-10-23) ist das `rma/:id/neu`.** Bis dahin lag die neue
 * Ansicht auf `rma/:id`; der Owner hat Classic wieder zum Standard gemacht
 * (*"Ja, Classic als standard wieder setzen"*), weil die neue Ansicht am
 * Telefon abstuerzt. Wer die Route in `App.js` verschiebt, zieht diese Zeile
 * mit: `route.test.js` haelt beide gegeneinander.
 */
export const FALL_ROUTE = 'rma/:id/neu'

/**
 * Die einsegmentigen statischen `rma/*`-Geschwister aus `App.js`.
 *
 * Sie werden VOR `rma/:id` gerankt und sind NICHT die Fallansicht. Kommt in
 * `App.js` ein weiteres dazu, gehoert es hierher - `route.test.js` liest die
 * Routentabelle und wird sonst rot.
 *
 * ⚠️ **Seit W4 (2026-10-23) faengt `FALL_ROUTE` sie ohnehin nicht mehr**: das
 * Muster ist jetzt zweisegmentig (`rma/:id/neu`), und `/rma/inbox` hat nur ein
 * Segment. Die Liste bleibt trotzdem stehen, und zwar als Gurt fuer den
 * Rueckbau: wer `FALL_ROUTE` wieder auf `rma/:id` setzt, liesse ohne sie die
 * Telefonleiste auf der Inbox und im Anlegepfad aus - ein Fehler, den niemand
 * sieht, weil beide Seiten dasselbe Bild zeigen (Regel 17 §1).
 */
export const RMA_STATISCH = new Set([
  'inbox',
  'new',
  'reports',
  'sla-overview',
  'faelligkeit',
  'inquiry-knowledge',
  'board',
])

/**
 * Ist dieser Pfad die neue Fallansicht?
 *
 * @param {string} pfadname - `location.pathname`
 * @returns {boolean} true nur fuer `rma/<id>/neu`, nicht fuer `rma/<id>`,
 *   `rma/<id>/classic` oder eines der statischen Geschwister
 */
/**
 * Das Suffix der ALTEN Fallansicht  -  an EINER Stelle.
 *
 * 🔴 **Warum die alte Ansicht ihre eigenen Verweise baut.** Ihre Kind- und
 * Elternfall-Verweise sind `RouterLink`s, also ein Wechsel INNERHALB der
 * Ansicht, ohne Neuladen. Zeigten sie auf die Adresse der NEUEN Ansicht, risse
 * jeder Klick den Rueckfall mitten in die neue Ansicht  -  und die Zusicherung
 * „die Unterdrueckung eines Hinweises gilt je Fall, nicht je Hinweisart"
 * (`detail-v2-cta-home-and-nudge` N.1/N.2) waere nicht mehr messbar: sie setzt
 * genau einen solchen Wechsel VORAUS. Gemessen am 2026-10-14, vor dieser
 * Aenderung: N.1 und N.2 rot, weil `v2.situation` nach dem Klick nicht mehr da
 * war.
 *
 * 🔴 **Seit W4 (2026-10-23) ist Classic der Standard.** `rma/:id` ist die alte
 * Ansicht; `rma/:id/classic` bleibt als ausdrueckliche Adresse gueltig (der
 * Menuepunkt „Open classic view" der neuen Ansicht baut sie). Beide Formen
 * zeigen dieselbe Flaeche  -  `fallAdresse(id, true)` liefert darum weiter
 * `/classic`, und die Kind-Verweise in `RMADetail` bleiben damit in Classic.
 */
export const KLASSISCH = 'classic'

/**
 * Die Adresse eines Falls in der jeweiligen Ansicht.
 *
 * @param {string|number} id - die Fallkennung
 * @param {boolean} [klassisch] - true = alte Ansicht (`rma/<id>/classic`),
 *   false = neue Ansicht (`rma/<id>/neu`)
 * @returns {string} die Adresse; beide Formen entstehen NUR hier
 */
export function fallAdresse(id, klassisch = false) {
  return klassisch ? `/rma/${id}/${KLASSISCH}` : `/rma/${id}/neu`
}

export function istFallansicht(pfadname) {
  if (typeof pfadname !== 'string' || !pfadname) return false
  const treffer = matchPath(FALL_ROUTE, pfadname)
  if (!treffer) return false
  return !RMA_STATISCH.has(treffer.params.id)
}
